import React from 'react';
import { Info, Car, Home, FileText, Calculator, PiggyBank, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о налоге на роскошь и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Налог на роскошь в России: всё, что нужно знать
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Car className="w-5 h-5 text-blue-500" />
          Что такое налог на роскошь для автомобилей?
        </h3>
        <p>
          <strong>Транспортный налог с повышающим коэффициентом</strong> (неофициально называемый «налогом на роскошь») в России применяется к автомобилям стоимостью от 3 млн рублей. Размер коэффициента зависит от стоимости и возраста автомобиля и варьируется от 1.1 до 3.
        </p>

        <h3 className="flex items-center gap-2">
          <Home className="w-5 h-5 text-blue-500" />
          Налог на роскошную недвижимость
        </h3>
        <p>
          В отношении объектов недвижимости повышенная ставка налога (2%) применяется к жилым помещениям, кадастровая стоимость которых превышает 300 млн рублей. Для других объектов могут применяться различные региональные ставки.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-blue-500" />
          Как рассчитывается налог на роскошные автомобили?
        </h3>
        <p>
          Расчет производится по формуле: <code>Налог = Мощность (л.с.) × Ставка × Повышающий коэффициент</code>.
        </p>
        <p>
          Повышающие коэффициенты:
        </p>
        <ul>
          <li><strong>1.1</strong> — для автомобилей стоимостью от 3 до 5 млн рублей возрастом до 3 лет</li>
          <li><strong>2</strong> — для автомобилей стоимостью от 5 до 10 млн рублей возрастом до 5 лет</li>
          <li><strong>3</strong> — для автомобилей стоимостью от 10 до 15 млн рублей возрастом до 10 лет</li>
          <li><strong>3</strong> — для автомобилей стоимостью от 15 млн рублей возрастом до 20 лет</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Список роскошных автомобилей</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Ежегодно Минпромторг России публикует перечень автомобилей, в отношении которых применяется повышающий коэффициент. Перечень обновляется и публикуется на официальном сайте министерства.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-500" />
          Законодательная база
        </h3>
        <p>
          Налог на роскошь регулируется следующими нормативными актами:
        </p>
        <ul>
          <li>Налоговый кодекс РФ (статья 362 пункт 2)</li>
          <li>Ежегодный перечень Минпромторга России</li>
          <li>Региональные законы о налоге на имущество физических лиц</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-green-500" />
          Кто имеет право на льготы?
        </h3>
        <p>
          От уплаты налога на имущество физических лиц полностью освобождаются:
        </p>
        <ul>
          <li>Инвалиды I и II групп</li>
          <li>Ветераны Великой Отечественной войны и боевых действий</li>
          <li>Пенсионеры</li>
          <li>Военнослужащие (частично)</li>
          <li>Многодетные семьи (в некоторых регионах)</li>
        </ul>
        <p>
          Для транспортного налога действуют региональные льготы, которые могут существенно различаться в разных субъектах РФ. Например, во многих регионах освобождаются от налога владельцы электромобилей.
        </p>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Важно знать
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Льготы по налогу на имущество предоставляются только на один объект каждого вида (одна квартира, один дом и т.д.)</li>
            <li>Льготы по транспортному налогу часто ограничиваются мощностью двигателя (как правило, до 150-200 л.с.)</li>
            <li>Для получения льготы необходимо подать заявление в налоговый орган</li>
            <li>При наличии задолженности по налогам льготы могут не предоставляться</li>
          </ul>
        </div>

        <h3>Как уменьшить налоговую нагрузку?</h3>
        <ul>
          <li><strong>Для автомобилей:</strong> Выбирать модели, не входящие в перечень роскошных, или приобретать авто старше 3-5 лет</li>
          <li><strong>Для недвижимости:</strong> Оспорить кадастровую стоимость, если она существенно выше рыночной</li>
          <li><strong>Для обоих видов:</strong> Оформить имущество на льготников (при наличии в семье)</li>
          <li><strong>Юридически:</strong> Уточнить региональные льготы и использовать их максимально</li>
        </ul>

        <h3>Сроки уплаты налогов</h3>
        <p>
          Налог на имущество физических лиц и транспортный налог необходимо уплатить не позднее 1 декабря года, следующего за истекшим налоговым периодом. Так, налог за 2024 год нужно будет уплатить до 1 декабря 2025 года.
        </p>
        <p>
          Налоговые уведомления обычно направляются не позднее 30 дней до наступления срока платежа. Если сумма налога меньше 100 рублей, уведомление не направляется.
        </p>

        <h3>Что будет, если не заплатить?</h3>
        <p>
          За неуплату налога начисляются пени за каждый день просрочки. В дальнейшем налоговая инспекция может:
        </p>
        <ol>
          <li>Направить требование об уплате налога</li>
          <li>Обратиться в суд с заявлением о взыскании налога</li>
          <li>Наложить арест на имущество должника</li>
          <li>Ограничить выезд за пределы РФ</li>
        </ol>

        <p className="font-semibold mt-6">
          Наш калькулятор поможет вам оценить налоговую нагрузку и спланировать свой бюджет с учетом всех коэффициентов и льгот!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;